/**
 * Paywall — 5-tier subscription screen with 7-day free trials.
 *
 * Tiers (matches revenuecat.js product IDs):
 *   Athlete             $4.99 / mo   · $49.99 / yr
 *   Agent Solo          $9.99 / mo   · $99.99 / yr
 *   Program Team 10     $49.99 / mo  · $499.99 / yr
 *   Program Team 20     $59.99 / mo  · $599.99 / yr
 *   Program Team 30     $79.99 / mo  · $799.99 / yr
 *
 * Behaviour:
 *   · Persona-filtered by default (Athletes see Athlete tier, Agents see Agent
 *     Solo, Programs/Coaches see the 3 Team tiers). Unknown personas + a
 *     "Show all plans" toggle reveal every tier.
 *   · Monthly / Annual toggle at the top (annual saves ~17%).
 *   · Primary CTA: "Start 7-day free trial" — kicks off StoreKit purchase
 *     via RevenueCat on iOS.
 *   · On web (RevenueCat unavailable) the CTA swaps to "Open in the FLYT
 *     iOS app to subscribe" — App Store guideline 3.1.1 forbids web purchase
 *     of digital subs anyway.
 *   · Restore Purchases button for previously-subscribed users on a new
 *     device.
 *
 * Rendered both as a modal (via PaywallContext) and as a full-screen page
 * (/upgrade route). The `variant` prop controls chrome around the body.
 */
import { useEffect, useMemo, useState } from 'react';
import { X, Check, Crown, Sparkles, Loader2, ExternalLink, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useUserAuth } from '../contexts/UserAuthContext';
import { isNativeApp } from '../lib/nativeBridge';
import {
  isRevenueCatAvailable,
  configureRevenueCat,
  getAvailablePackages,
  purchasePackage,
  purchaseProductById,
  restorePurchases,
} from '../lib/revenuecat';

const TIERS = [
  {
    id: 'athlete',
    name: 'Athlete',
    tagline: 'Your live FLYT ID™ + full career page.',
    accent: '#60A5FA', // blue
    icon: Sparkles,
    monthly: { price: 4.99, productId: 'com.flytstudios.league.athlete.monthly' },
    annual: { price: 49.99, productId: 'com.flytstudios.league.athlete.annual' },
    personas: ['athlete'],
    features: [
      'Public /l/… bio link + share graphics',
      'Push to Network — full Pulse feed',
      'Career page, media reel, brand badges',
      'Priority claim + verification review',
    ],
  },
  {
    id: 'agent-solo',
    name: 'Agent Solo',
    tagline: 'Represent up to 4 athletes.',
    accent: '#22D3EE', // cyan
    icon: Crown,
    monthly: { price: 9.99, productId: 'com.flytstudios.league.agent.solo.monthly' },
    annual: { price: 99.99, productId: 'com.flytstudios.league.agent.solo.annual' },
    personas: ['agent'],
    features: [
      'Agent /l/agent/… public roster page',
      'Represent up to 4 athletes',
      'Covers every athlete’s Premium',
      'Client portal, deal desk, payouts',
    ],
  },
  {
    id: 'program-team10',
    name: 'Team · 10',
    tagline: 'Programs up to 10 athletes.',
    accent: '#F59E0B', // amber
    icon: Crown,
    monthly: { price: 49.99, productId: 'com.flytstudios.league.program.team10.monthly' },
    annual: { price: 499.99, productId: 'com.flytstudios.league.program.team10.annual.v2' },
    personas: ['program', 'coach'],
    features: [
      'Program dashboard + team roster',
      'Up to 10 athletes covered',
      'Every athlete’s Premium included',
      'Coach analytics + parent portal',
    ],
  },
  {
    id: 'program-team20',
    name: 'Team · 20',
    tagline: 'Programs up to 20 athletes.',
    accent: '#FB923C', // orange
    icon: Crown,
    monthly: { price: 59.99, productId: 'com.flytstudios.league.program.team20.monthly' },
    annual: { price: 599.99, productId: 'com.flytstudios.league.program.team20.annual' },
    personas: ['program', 'coach'],
    features: [
      'Program dashboard + team roster',
      'Up to 20 athletes covered',
      'Every athlete’s Premium included',
      'Bulk invites, CSV upload',
    ],
  },
  {
    id: 'program-team30',
    name: 'Team · 30',
    tagline: 'Programs up to 30 athletes.',
    accent: '#F43F5E', // rose
    icon: Crown,
    monthly: { price: 79.99, productId: 'com.flytstudios.league.program.team30.monthly' },
    annual: { price: 799.99, productId: 'com.flytstudios.league.program.team30.annual' },
    personas: ['program', 'coach'],
    features: [
      'Program dashboard + team roster',
      'Up to 30 athletes covered',
      'Every athlete’s Premium included',
      'Priority support, dedicated CSM',
    ],
  },
];

const REASON_COPY = {
  analytics: {
    kicker: 'Analytics · Premium',
    title: 'Unlock the full analytics view.',
    body: 'See deep performance stats, share reach, and career trends behind Premium.',
  },
  roster: {
    kicker: 'Full Roster · Premium',
    title: 'See the whole roster.',
    body: 'Agent Solo unlocks unlimited roster views + representation tools.',
  },
  ambassador: {
    kicker: 'Ambassador · Premium',
    title: 'Boost your Ambassador Program.',
    body: 'Premium unlocks payouts, tracking, and rich referral graphics.',
  },
  bio: {
    kicker: 'FLYT ID™ · Premium',
    title: 'Go live with your bio link.',
    body: 'Publish your /l/… page + share graphics + priority claim review.',
  },
  default: {
    kicker: 'FLYT · Premium',
    title: 'Go Premium.',
    body: 'Start your 7-day free trial. Cancel anytime in Settings › Apple ID › Subscriptions.',
  },
};

/**
 * Match a raw product ID from a RevenueCat package to our TIERS map so we
 * can hand the *real* Package object to purchasePackage(). This is needed
 * because RC's package identifiers are opaque — the product ID is what
 * matches App Store Connect.
 */
function findPackageForProductId(packages, productId) {
  return packages.find(
    (p) => p?.product?.identifier === productId || p?.storeProduct?.identifier === productId
  );
}

export function Paywall({ variant = 'modal', reason = 'default', onClose }) {
  const { user } = useUserAuth();
  const [period, setPeriod] = useState('annual');
  const [showAll, setShowAll] = useState(false);
  const [packages, setPackages] = useState([]);
  const [loadingPkgs, setLoadingPkgs] = useState(true);
  const [busyTier, setBusyTier] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const nativeAvailable = isRevenueCatAvailable();
  // Are we physically inside the iOS shell? Distinct from nativeAvailable —
  // if the RevenueCat key is missing from a build, nativeAvailable is false
  // but we must NEVER show "Open in FLYT iOS app" to someone already in it.
  const onNativeIOS = isNativeApp();

  // Persona filter — but if we don't recognise the persona, show everything.
  const persona = (user?.persona || '').toLowerCase();
  const visibleTiers = useMemo(() => {
    if (showAll) return TIERS;
    const matching = TIERS.filter((t) => t.personas.includes(persona));
    return matching.length ? matching : TIERS;
  }, [persona, showAll]);

  const copy = REASON_COPY[reason] || REASON_COPY.default;

  // Pull the live offerings once when opened on iOS. On web we skip this —
  // the CTA already routes users to the iOS app.
  useEffect(() => {
    let cancelled = false;
    if (!nativeAvailable) { setLoadingPkgs(false); return; }
    (async () => {
      // iter546 · Ensure the SDK is configured before we hit the
      // offerings endpoint. The boot-time configure in UserAuthContext
      // is fire-and-forget, so the paywall can open before it finishes
      // and return `not_available` on every purchase attempt. Calling
      // configure here is idempotent (revenuecat.js guards with
      // `_configured` flag), so it's safe to re-run.
      try { await configureRevenueCat(user?.user_id); } catch { /* noop */ }
      const pkgs = await getAvailablePackages();
      if (!cancelled) {
        setPackages(pkgs || []);
        setLoadingPkgs(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nativeAvailable, user?.user_id]);

  const handlePurchase = async (tier) => {
    const productId = tier[period].productId;
    if (!nativeAvailable) {
      if (onNativeIOS) {
        toast.error('Purchases aren’t available in this version — please update FLYT and try again.');
      } else {
        toast.info('Open FLYT on your iPhone to start your free trial.');
      }
      return;
    }
    setBusyTier(tier.id);
    try {
      // Primary path — use the RC "Current" offering package if it's
      // populated. Cleanest UX + full RC attribution + trial eligibility.
      const pkg = findPackageForProductId(packages, productId);
      let res;
      if (pkg) {
        res = await purchasePackage(pkg);
      } else {
        // iter543 · Fallback — offering isn't populated (products still
        // `In Review` in ASC, or no "Current" offering set on the RC
        // dashboard yet). Purchase the product directly by its ASC
        // identifier. Same StoreKit sheet, same webhook, no dependency
        // on offering config. Mirrors ReviewerUpgradeCTA.jsx behaviour.
        res = await purchaseProductById(productId);
      }
      if (res.entitlement) {
        toast.success('You’re in — welcome to Premium.');
        onClose?.();
      } else if (res.cancelled) {
        /* silent — user tapped cancel on the Apple sheet */
      } else if (res.error) {
        // iter540 · surface the underlying StoreKit / RC error verbatim
        // so App Review + we can see WHY the sheet didn't open (usually
        // "Sandbox tester not signed in", "product not ready in ASC",
        // or "screen-time restriction"). Previously we showed a bland
        // "Purchase failed" toast which hid the actual cause.
        const detail = typeof res.error === 'string'
          ? res.error
          : (res.error?.message || res.error?.underlyingErrorMessage || 'unknown error');
        toast.error(`Purchase failed: ${detail}`, { duration: 8000 });
      } else {
        // No entitlement, no cancel, no error object — SDK returned
        // nothing at all. Almost always means Sandbox Tester isn't
        // configured on the device.
        toast.error(
          'StoreKit didn’t open. Sign in as a Sandbox Tester under Settings → App Store → Sandbox Account (US storefront), then try again.',
          { duration: 10000 },
        );
      }
    } catch (e) {
      // iter540 · Same surface for thrown errors (SDK bug, missing
      // entitlement, product not found in RC offering, etc.)
      const detail = e?.message || e?.userInfo?.readableErrorCode || String(e);
      toast.error(`StoreKit error: ${detail}`, { duration: 8000 });
    } finally {
      setBusyTier(null);
    }
  };

  const handleRestore = async () => {
    if (!nativeAvailable) {
      if (onNativeIOS) {
        toast.error('Restore isn’t available in this version — please update FLYT and try again.');
      } else {
        toast.info('Restore is only available in the FLYT iOS app.');
      }
      return;
    }
    setRestoring(true);
    try {
      const res = await restorePurchases();
      if (res.entitlement) {
        toast.success('Purchases restored.');
        onClose?.();
      } else if (res.error) {
        // iter543 · mirror the purchase-path diagnostic. If StoreKit
        // errors (sandbox misconfig, network, RC config), surface the
        // real cause instead of the generic "no active subs" toast so
        // App Review can debug.
        toast.error(`Restore failed: ${res.error}`, { duration: 8000 });
      } else {
        toast.info('No active subscriptions found on this Apple ID.');
      }
    } catch (e) {
      const detail = e?.message || e?.userInfo?.readableErrorCode || String(e);
      toast.error(`Restore error: ${detail}`, { duration: 8000 });
    } finally {
      setRestoring(false);
    }
  };

  // ─── Body ─────────────────────────────────────────────────────────────
  const body = (
    <div className="w-full text-white" data-testid="paywall-body">
      {/* Header */}
      <div className="mb-6">
        <p className="font-italic text-[11px] uppercase tracking-widest text-blue-300 mb-1.5" data-testid="paywall-kicker">
          {copy.kicker}
        </p>
        <h2 className="font-display text-3xl sm:text-4xl uppercase leading-[1.05]" data-testid="paywall-title">
          {copy.title}
        </h2>
        <p className="mt-3 text-sm text-white/70 max-w-xl leading-relaxed" data-testid="paywall-body-copy">
          {copy.body}
        </p>
      </div>

      {/* Monthly / Annual toggle */}
      <div className="inline-flex items-center rounded-full bg-white/5 ring-1 ring-white/10 p-1 mb-6" data-testid="paywall-period-toggle">
        <button
          type="button"
          onClick={() => setPeriod('monthly')}
          className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded-full transition ${
            period === 'monthly' ? 'bg-white text-black' : 'text-white/60 hover:text-white'
          }`}
          data-testid="paywall-toggle-monthly"
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => setPeriod('annual')}
          className={`px-4 py-1.5 text-xs uppercase tracking-widest font-bold rounded-full transition ${
            period === 'annual' ? 'bg-white text-black' : 'text-white/60 hover:text-white'
          }`}
          data-testid="paywall-toggle-annual"
        >
          Annual <span className="ml-1 text-[10px] text-emerald-400">Save ~17%</span>
        </button>
      </div>

      {/* Tiers */}
      <div
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        data-testid="paywall-tiers"
      >
        {loadingPkgs && nativeAvailable ? (
          <div className="col-span-full flex items-center justify-center py-10 text-white/50">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plans…
          </div>
        ) : (
          visibleTiers.map((tier) => {
            const Icon = tier.icon;
            const price = tier[period].price;
            // iter874 · App Review 3.1.2: show the price Apple will actually
            // charge (localized StoreKit string) whenever the package is
            // loaded; the hard-coded USD price is only a fallback.
            const storePrice = findPackageForProductId(packages, tier[period].productId)?.product?.priceString;
            const priceLabel = storePrice || `$${price.toFixed(2)}`;
            const per = period === 'monthly' ? '/mo' : '/yr';
            const isBusy = busyTier === tier.id;
            return (
              <div
                key={tier.id}
                className="rounded-2xl p-5 ring-1 flex flex-col"
                style={{
                  background: `linear-gradient(160deg, ${tier.accent}22, rgba(0,0,0,0) 60%)`,
                  borderColor: `${tier.accent}55`,
                }}
                data-testid={`paywall-tier-${tier.id}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="w-4 h-4" style={{ color: tier.accent }} />
                  <p className="text-xs uppercase tracking-widest font-bold">{tier.name}</p>
                </div>
                <p className="text-sm text-white/70 mb-4 leading-relaxed">{tier.tagline}</p>
                <div className="mb-4">
                  <span className="font-display text-3xl">{priceLabel}</span>
                  <span className="text-white/60 text-sm ml-1">{per}</span>
                </div>
                <ul className="space-y-2 mb-5 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/80 leading-snug">
                      <Check className="w-3.5 h-3.5 mt-1 shrink-0" style={{ color: tier.accent }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => handlePurchase(tier)}
                  disabled={isBusy || (!nativeAvailable && false /* still allow tap for web toast */)}
                  className="w-full rounded-full px-4 py-2.5 text-xs uppercase tracking-widest font-bold bg-white text-black hover:bg-white/90 disabled:opacity-50 transition inline-flex items-center justify-center gap-1.5"
                  data-testid={`paywall-cta-${tier.id}`}
                >
                  {isBusy ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Opening…</>
                  ) : !nativeAvailable && !onNativeIOS ? (
                    <>Open in FLYT iOS app <ExternalLink className="w-3.5 h-3.5" /></>
                  ) : (
                    <>Start 7-day free trial</>
                  )}
                </button>
                <p className="text-[10px] text-white/40 mt-2 text-center">
                  Then {priceLabel}{per}. Cancel anytime.
                </p>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] text-white/50">
        {!showAll && visibleTiers.length < TIERS.length && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="underline underline-offset-4 hover:text-white"
            data-testid="paywall-show-all"
          >
            Show all plans
          </button>
        )}
        <button
          type="button"
          onClick={handleRestore}
          disabled={restoring || (!nativeAvailable && !onNativeIOS)}
          className="inline-flex items-center gap-1.5 underline underline-offset-4 hover:text-white disabled:opacity-40"
          data-testid="paywall-restore"
        >
          <RotateCcw className="w-3 h-3" />
          {restoring ? 'Restoring…' : 'Restore purchases'}
        </button>
        <a
          href="/terms"
          className="underline underline-offset-4 hover:text-white"
          data-testid="paywall-terms-link"
        >
          Terms
        </a>
        <a
          href="/privacy"
          className="underline underline-offset-4 hover:text-white"
          data-testid="paywall-privacy-link"
        >
          Privacy
        </a>
      </div>
      <p className="mt-4 text-[10px] text-white/40 leading-relaxed max-w-xl">
        Payment is charged to your Apple ID at confirmation of purchase. Subscription auto-renews
        unless cancelled at least 24 hours before the end of the current period. Manage or cancel
        anytime in Settings › Apple ID › Subscriptions.
      </p>
    </div>
  );

  // ─── Chrome ───────────────────────────────────────────────────────────
  if (variant === 'page') {
    return (
      <div className="min-h-screen bg-black px-4 py-10" data-testid="paywall-page">
        <div className="max-w-5xl mx-auto">
          {body}
        </div>
      </div>
    );
  }

  // Modal
  return (
    <div
      className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-6"
      onClick={onClose}
      data-testid="paywall-modal-backdrop"
    >
      <div
        className="relative w-full max-w-5xl max-h-[95vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-neutral-950 ring-1 ring-white/10 p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="paywall-modal"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          aria-label="Close"
          data-testid="paywall-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>
        {body}
      </div>
    </div>
  );
}

export default Paywall;
